import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import toast from "react-hot-toast";
import { FiFilter, FiInfo, FiPlus, FiTrash2, FiUser } from "react-icons/fi";
import { CommonTable, Loader } from "../../Components/Common";
import CommonFilter from "../../Components/Common/CommonFilter";
import {
  GET_ALL_PAGINATION,
  RESET_PAGINATION,
  ROLES,
  USER_ROLES,
} from "../../Constants/Strings";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";
import {
  REFERRAL_COLUMNS,
  REFERRAL_COLUMNS_LABELS,
  REFERRAL_STATUS,
  REFERRAL_STATUS_COLORS,
  REFERRAL_STATUS_LABELS,
  TOAST_MESSAGES,
} from "./constants";
import { toProperCase } from "../../Utils";
import { UserContext } from "../../Context";
import { getAllUsers } from "../../Services/UserService";
import { deleteReferral, getReferrals } from "../../Services/essServices";
import * as routes from "../../routes";

const EmployeeReferralList = () => {
  const [referralData, setReferralData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState(RESET_PAGINATION);
  const location = useLocation();
  const [filters, setFilters] = useState(() => {
    return (
      location.state?.filters || {
        status: "",
        referred_by: "",
        is_declaration_signed: "",
      }
    );
  });
  const [tempFilters, setTempFilters] = useState(filters);
  const [showFilter, setShowFilter] = useState(false);
  const navigate = useNavigate();
  const { user } = useContext(UserContext);

  useEffect(() => {
    fetchEmployeeReferrals(
      { offset: 0, limit: pagination.limit },
      true,
      filters
    );
  }, [filters]);

  const fetchEmployeeReferrals = async (
    params,
    isInitial = false,
    currentFilters = filters
  ) => {
    setLoading(true);
    try {
      const cleanedFilters = Object.fromEntries(
        Object.entries(currentFilters).filter(([_, val]) => val !== "")
      );

      const res = await getReferrals({ ...params, ...cleanedFilters });

      if (!res || !res.data) {
        throw new Error(TOAST_MESSAGES.NO_RESPONSE_DATA);
      }

      const results = res.data.results || [];
      setReferralData((prev) => (isInitial ? results : [...prev, ...results]));
      setPagination((prev) => ({
        ...prev,
        count: res.data.count || 0,
        offset: isInitial ? results.length : prev.offset + results.length,
      }));
    } catch (err) {
      toast.error(TOAST_MESSAGES.FAILED_FETCH_LEAVES);
      if (isInitial) {
        setReferralData([]);
        setPagination((prev) => ({ ...prev, count: 0, offset: 0 }));
      }
    } finally {
      setLoading(false);
    }
  };

  const loadMoreReferralData = () => {
    if (pagination.offset >= pagination.count) return;
    fetchEmployeeReferrals({
      offset: pagination.offset,
      limit: pagination.limit,
    });
  };

  useEffect(() => {
    const payload = { mode: "all", ...GET_ALL_PAGINATION };
    const fetchUsers = async () => {
      try {
        const res = (await getAllUsers(payload)).data.results;
        setUsers(res);
      } catch (err) {
        const msg = extractErrorMessage(err) || TOAST_MESSAGES.USER_FETCH_ERROR;
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleApplyFilters = () => {
    setFilters(tempFilters);
    setPagination(RESET_PAGINATION);
    setReferralData([]);
    setShowFilter(false);

    // Update navigation state for back button
    navigate(".", { state: { filters: tempFilters }, replace: true });
  };

  const handleClearFilters = () => {
    const cleared = { status: "", referred_by: "", is_declaration_signed: "" };
    setTempFilters(cleared);
    setFilters(cleared);
    setPagination(RESET_PAGINATION);
    setReferralData([]);

    navigate(".", { state: { filters: cleared }, replace: true });
  };

  const handleCancelFilters = () => {
    setTempFilters(filters);
    setShowFilter(false);
  };

    const handleReferralDelete = async (referral_id) => {
      try {
        const response = await deleteReferral(referral_id);
        if (response.status === 204) {
          toast.success(TOAST_MESSAGES.DELETE_REFERRAL);
          setReferralData((prev) =>
            prev.filter((item) => item.id !== referral_id)
          );

          setPagination((prev) => ({
            ...prev,
            count: Math.max(prev.count - 1, 0),
            offset: Math.max(prev.offset - 1, 0),
          }));
        } else {
          const msg =
            extractErrorMessage(response) ||
            TOAST_MESSAGES.FAILED_TO_DELETE_REFERRAL;
          toast.error(msg);
        }
      } catch (error) {
        const msg =
          extractErrorMessage(error) ||
          TOAST_MESSAGES.FAILED_TO_DELETE_REFERRAL;
        toast.error(msg);
      }
    };  


  const handleRenderField = (key, row) => {
    const value = row[key] || "-";
    switch (key) {
      case "status":
        return (
          <span
            className={`px-2 py-1 rounded-md text-xs font-medium
            ${
              REFERRAL_STATUS_COLORS[row.status] || "bg-gray-100 text-gray-700"
            }`}
          >
            {toProperCase(row.status)}
          </span>
        );
      case "is_declaration_signed":
        return <div>{row.is_declaration_signed ? "Yes" : "No"}</div>;
      case "actions":
        return (
          <div
            className={`grid grid-cols-2 items-center justify-center text-xl text-center`}
          >
            <FiInfo
              className="text-green-600 text-xl cursor-pointer"
              onClick={() =>
                navigate(
                  routes.VIEW_EMP_REFERRAL.replace(":instanceId", row.id),
                  {
                    state: {
                      filters,
                      fromPage: routes.EMP_REFERRAL_LIST,
                    },
                  }
                )
              }
              title={"View Details"}
            />
            <button
              disabled={row.status !== REFERRAL_STATUS_LABELS.REFERRED}
              className="text-red-600 text-xl cursor-pointer disabled:cursor-not-allowed disabled:opacity-70 disabled:text-gray-500"
              onClick={(e) =>{ 
                e.stopPropagation();
                 if (window.confirm("Are you sure you want to delete this referral?"))
                handleReferralDelete(row.id, REFERRAL_STATUS_LABELS.REFERRED);
              }}
              title={
                row.status !== REFERRAL_STATUS_LABELS.REFERRED
                  ? `Can not be deleted in ${toProperCase(row.status)} state.`
                  : "Delete"
              }
            >
              <FiTrash2 />
            </button>
          </div>
        );
      default:
        return value;
    }
  };

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-primary">
          {[USER_ROLES.HR, USER_ROLES.ADMIN, USER_ROLES.DIRECTOR_MANAGEMENT].includes(user.role)
            ? "Employee Referrals"
            : "My Referrals"}
        </h2>

        <div className="flex gap-2 items-center">
          <button
            className="px-4 py-2 rounded bg-primary hover:bg-green-600 text-white font-bold shadow-lg flex items-center gap-1"
            onClick={() => {
              setTempFilters(filters);
              setShowFilter(true);
            }}
          >
            <FiFilter className="text-xl" />
          </button>
          <button
            className="hidden md:block px-4 py-1.5 rounded bg-primary hover:bg-green-600 text-white font-medium shadow-lg items-center gap-1"
            onClick={() => navigate(routes.ADD_EMP_REFERRAL)}
          >
            {" "}
            Add Referral
          </button>

          <button
            onClick={() => navigate(routes.ADD_EMP_REFERRAL)}
            className="bg-primary text-white px-4 py-2 rounded-md hover:bg-green-600 text-sm block md:hidden"
          >
            <FiPlus className="text-xl font-bold" />
          </button>
        </div>
      </div>

      {/* Filter Modal */}
      {showFilter && (
        <CommonFilter
          title="Employee Referrals Filter"
          tempFilters={tempFilters}
          setTempFilters={setTempFilters}
          onApply={handleApplyFilters}
          onClear={handleClearFilters}
          onCancel={handleCancelFilters}
          userOptions={user.role in [ROLES.HR, ROLES.DIRECTOR_MANAGEMENT] ? users : []}
          useSearchableUser={user.role in [ROLES.HR, ROLES.DIRECTOR_MANAGEMENT] ? true : false}
          setUserFilterField={"referred_by"}
          customLabel={"Referred By"}
          statusOptions={[{ label: "All", value: "" }, ...REFERRAL_STATUS]}
          filtersConfig={[
            {
              type: "boolean",
              key: "is_declaration_signed",
              label: "Declaration Signed",
            },
          ]}
        />
      )}

      {/* Loader or Data */}
      {loading && referralData.length === 0 ? (
        <div className="flex justify-center pt-10">
          <Loader size={4} />
        </div>
      ) : referralData.length === 0 ? (
        <div className="text-center text-gray-500 mt-10">
          No Referrals Available
        </div>
      ) : (
        <>
          {/* Desktop View */}
          <div className="hidden md:block">
            <InfiniteScroll
              dataLength={referralData.length}
              next={loadMoreReferralData}
              hasMore={pagination.offset < pagination.count}
              scrollableTarget="desktopReferralData"
            >
              <div
                id="desktopReferralData"
                className="h-[68vh] overflow-y-auto border rounded-md"
              >
                <CommonTable
                  tableColumns={REFERRAL_COLUMNS}
                  tableRows={referralData}
                  columnLabels={REFERRAL_COLUMNS_LABELS}
                  renderField={handleRenderField}
                />
              </div>
            </InfiniteScroll>
          </div>
        </>
      )}

      {/* MOBILE VIEW (Card UI) */}
      <div className="md:hidden">
        <InfiniteScroll
          dataLength={referralData.length}
          next={loadMoreReferralData}
          hasMore={pagination.offset < pagination.count}
          scrollableTarget="mobileReferralData"
        >
          <div
            id="mobileReferralData"
            className="h-[75vh] overflow-y-auto space-y-2 p-1"
          >
            {referralData.map((item) => (
              <div
                key={item.id}
                className="bg-white shadow-lg rounded-xl p-4 border border-gray-200 transition-all duration-300 hover:shadow-xl"
                onClick={() =>
                  navigate(
                    routes.VIEW_EMP_REFERRAL.replace(":instanceId", item.id),
                    {
                      state: {
                        filters,
                        fromPage: routes.EMP_REFERRAL_LIST,
                      },
                    }
                  )
                }
              >
                {/* Header: Referred Person */}
                <div className="flex justify-between items-center mb-1 gap-2">
                  <h3 className="flex items-center gap-2 font-semibold text-base text-gray-900 min-w-0">
                    <FiUser size={18} className="shrink-0" />
                    <span className="truncate">
                      {item.referred_person_name || "-"}
                    </span>
                  </h3>

                  <span
                    className={`shrink-0 px-2 py-1 rounded-md text-xs font-medium ${
                      REFERRAL_STATUS_COLORS[item.status] ||
                      "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {toProperCase(item.status)}
                  </span>
                </div>

                {/* Position + Mobile Number */}
                <div className="flex flex-col justify-between text-sm text-gray-700">
                  <span className="font-medium">
                    Position: {item.referred_position || "-"}
                  </span>
                  <span className="font-medium">
                    Contact: {item.mobile_number || "-"}
                  </span>
                </div>

                {/* Resume */}
                <div className="flex items-center justify-between">
                  {item.resume ? (
                    <a
                      href={item.resume}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary underline text-sm"
                    >
                      View Resume
                    </a>
                  ) : (
                    <p className="text-gray-500 text-sm">No resume uploaded</p>
                  )}
                  {/* delete button */}
                   <button
                    disabled={item.status !== REFERRAL_STATUS_LABELS.REFERRED}
                    className="text-red-600 text-xl cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (
                        window.confirm(
                          "Are you sure you want to delete this referral?"
                        )
                      ) {
                        handleReferralDelete(
                          item.id,
                          REFERRAL_STATUS_LABELS.REFERRED
                        );
                      }
                    }}
                    title={
                      item.status !== REFERRAL_STATUS_LABELS.REFERRED
                        ? `Can not be deleted in ${toProperCase(
                            item.status
                          )} state.`
                        : "Delete"
                    }
                  >
                    <FiTrash2 />
                  </button> 
                </div>
              </div>
            ))}
          </div>
        </InfiniteScroll>
      </div>
    </div>
  );
};

export default EmployeeReferralList;
